import { ReactElement, useLayoutEffect, useRef } from "react";
import "./slide-up.scss";

export type SlideUpItem = {
    id: number;
    element: ReactElement;
};

export type SlideUpProps = {
    items: SlideUpItem[];
    className?: string;
    options?: {
        maxVisibleItems?: number;
        gap?: number;
    };
};

const useWeakMap = <TKey extends object, TValue>() => {
    const mapRef = useRef<WeakMap<TKey, TValue> | undefined>(undefined);
    if (!mapRef.current) {
        mapRef.current = new WeakMap<TKey, TValue>();
    }
    return mapRef.current;
};

export default function SlideUp({ items, options, className }: SlideUpProps) {
    const itemRefs = useWeakMap<SlideUpItem, HTMLDivElement>();

    const maxVisibleItems = options?.maxVisibleItems ?? 3;
    const gap = options?.gap ?? 0;

    // Keep maxVisibleItems + 1 (for fade-out)
    const renderedItems = items.slice(-maxVisibleItems - 1);

    useLayoutEffect(() => {
        if (renderedItems.length === 0) return;

        const domElements = renderedItems.map(item => itemRefs.get(item)!);
        const elementHeights = domElements.map(el => el.getBoundingClientRect().height);

        let visibleHeight = 0;
        for (let i = Math.max(0, renderedItems.length - maxVisibleItems); i < renderedItems.length; i++) {
            visibleHeight += elementHeights[i];
        }
        const offset = visibleHeight / 2; // put da ting in da center

        // initial positions: last item off-screen
        let accumHeight = -elementHeights[elementHeights.length - 1] - offset;
        for (let i = renderedItems.length - 1; i >= 0; i--) {
            domElements[i].style.bottom = `${accumHeight}px`;
            accumHeight += elementHeights[i];
        }

        const translateTransitionConfig: KeyframeAnimationOptions = {
            duration: 400,
            easing: "ease-in-out",
            fill: "forwards"
        };

        requestAnimationFrame(() => {
            let accumHeight = -offset;
            for (let i = renderedItems.length - 1; i >= 0; i--) {
                domElements[i].animate([{ bottom: `${accumHeight}px` }], translateTransitionConfig);
                accumHeight += elementHeights[i];
            }

            if (renderedItems.length === maxVisibleItems + 1) {
                domElements[0].animate([{ opacity: 0 }], fadeoutTransitionConfig);
            }
        });
    }, [renderedItems, maxVisibleItems, gap]);

    return (
        <div className={`slide-up-container ${className ? className : ""}`}>
            <div className="floating-items-wrapper">
                {renderedItems.map(item => (
                    <div
                        key={item.id}
                        ref={domElement => {
                            if (domElement) {
                                itemRefs.set(item, domElement);
                            } else {
                                itemRefs.delete(item);
                            }
                        }}
                        className="slide-up-item"
                        style={{ paddingBlock: `${gap / 2}px` }}
                    >
                        {item.element}
                    </div>
                ))}
            </div>
        </div>
    );
}

const fadeoutTransitionConfig: KeyframeAnimationOptions = {
    duration: 500,
    easing: "ease-in-out",
    fill: "forwards"
};
